"use client";

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ProductChatItem from './ProductChatItem';
import ProductBidItem from './ProductBidItem';
import ProductChatForm from './ProductChatForm';
import {
  getProductChatsByProductId,
  getProductChatsByCatalogId,
  ProductChatItem as ProductChatItemType,
  getBidsByProductId,
  BidItem,
  supabase,
  getProfileById
} from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Session } from '@supabase/supabase-js';
import { Loader2, MessageSquare } from '@/components/icons';
import { Skeleton } from '@/components/ui/skeleton';
import { RealtimeChannel } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'framer-motion'; // Importar motion y AnimatePresence

// Define a union type for combined activity items
type ProductActivityItem = (ProductChatItemType & { type: 'chat' }) | (BidItem & { type: 'bid' });

interface ProductActivityFeedProps {
  productId?: string;
  productCatalogId?: string;
  session: Session | null;
  sellerId: string; // NEW: ID del vendedor
  isAuction?: boolean; // NEW: Whether this is an auction product
}

const ProductActivityFeed: React.FC<ProductActivityFeedProps> = ({ productId, productCatalogId, session, sellerId }) => {
  const [activities, setActivities] = useState<ProductActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const activityContainerRef = useRef<HTMLDivElement>(null);

  const currentUserId = session?.user?.id;
  const isProductOwner = currentUserId === sellerId;

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      // For catalog products, read from product_catalog_comments (RLS-protected)
      // For auction products, read from product_chats (open to all)
      const chatPromises: Promise<ProductChatItemType[]>[] = [];
      if (productCatalogId) {
        // Catalog products: read verified buyer comments from product_catalog_comments
        chatPromises.push(getProductChatsByCatalogId(productCatalogId));
      } else if (productId) {
        // Auction products: read all chats from product_chats
        chatPromises.push(getProductChatsByProductId(productId));
      }
      
      const [chatResults, fetchedBids] = await Promise.all([
        Promise.all(chatPromises),
        productId ? getBidsByProductId(productId) : Promise.resolve([]),
      ]);

      // Combine chats and remove duplicates by id
      const allChats = chatResults.flat();
      const uniqueChatsMap = new Map<string, ProductChatItemType>();
      allChats.forEach(chat => uniqueChatsMap.set(chat.id, chat));
      const uniqueChats = Array.from(uniqueChatsMap.values());

      const combinedActivities: ProductActivityItem[] = [
        ...uniqueChats.map(chat => ({ ...chat, type: 'chat' as const })),
        ...fetchedBids.map(bid => ({ ...bid, type: 'bid' as const })),
      ];

      // Sort by created_at in ASCENDING order (oldest first, newest at bottom)
      combinedActivities.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      setActivities(combinedActivities);
    } catch (error) {
      console.error("ProductActivityFeed ERROR: Error fetching product activities:", error);
      toast({
        title: "Error al cargar la actividad",
        description: "No se pudieron cargar los mensajes y pujas. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [productId, productCatalogId, toast]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Realtime subscription for product chats and bids
  useEffect(() => {
    const idForChannel = productCatalogId ?? productId;
    if (!idForChannel) {
      return;
    }


    const chatFilter = productCatalogId ? `product_catalog_id=eq.${productCatalogId}` : `product_id=eq.${productId}`;
    
    // For catalog products, subscribe to product_catalog_comments. For auctions, subscribe to product_chats
    const chatTableName = productCatalogId ? 'product_catalog_comments' : 'product_chats';

    const chatChannel: RealtimeChannel = supabase
      .channel(`product_comments_${idForChannel}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: chatTableName,
          filter: chatFilter
        },
        async (payload) => {
          if (payload.eventType === 'INSERT' && payload.new) {
            const newChat = payload.new as ProductChatItemType;
            const profile = await getProfileById(newChat.user_id);
            const chatWithProfile: ProductChatItemType = {
              ...newChat,
              profiles: profile ? { username: profile.username, avatar_url: profile.avatar_url } : null,
            };
            setActivities(prevActivities => {
              const updatedActivities = [...prevActivities, { ...chatWithProfile, type: 'chat' as const }];
              // Keep sorted by created_at in ASCENDING order
              updatedActivities.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
              return updatedActivities;
            });
          }
        }
      )
      .subscribe((status) => {
      });

    const bidChannel: RealtimeChannel = supabase
      .channel(`product_bids_${productId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bids',
          filter: `product_id=eq.${productId}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT' && payload.new) {
            const newBid = payload.new as BidItem;
            const profile = await getProfileById(newBid.bidder_id);
            const bidWithProfile: BidItem = {
              ...newBid,
              profiles: profile ? { username: profile.username, avatar_url: profile.avatar_url } : null,
            };
            setActivities(prevActivities => {
              const updatedActivities = [...prevActivities, { ...bidWithProfile, type: 'bid' as const }];
              // Keep sorted by created_at in ASCENDING order
              updatedActivities.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
              return updatedActivities;
            });
          }
        }
      )
      .subscribe((status) => {
      });

    return () => {
      supabase.removeChannel(chatChannel);
      supabase.removeChannel(bidChannel);
    };
  }, [productId, productCatalogId, session]);

  // Scroll to bottom on new activity
  useEffect(() => {
    if (activityContainerRef.current) {
      activityContainerRef.current.scrollTop = activityContainerRef.current.scrollHeight;
    }
  }, [activities]); // Depende de 'activities' para dispararse cuando la lista cambia

  // Para productos de catálogo, no mostramos el mensaje de "tu producto" ya que los chats son más generales
  const isCatalogProduct = !!productCatalogId;

  return (
    <div className="space-y-0">
      <div ref={activityContainerRef} className="space-y-3 max-h-60 overflow-y-auto overflow-x-hidden pr-2 border rounded-t-md p-2 bg-background">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : activities.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay comentarios
          </p>
        ) : (
          <AnimatePresence initial={false}> {/* initial={false} para que no anime los elementos iniciales */}
            {activities.map((activity) => (
              <motion.div
                key={activity.id} // `key` es crucial para AnimatePresence
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {activity.type === 'chat' ? (
                  <ProductChatItem chat={activity} />
                ) : (
                  <ProductBidItem bid={activity} />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
      
      {/* Chat Form integrated inline */}
      {productId && (
        <div className="border border-t-0 rounded-b-md bg-background p-2 relative z-10">
          <ProductChatForm
            productId={productId}
            session={session}
            isAuction={true}
            onChatAdded={() => {
              // Refresh activities will happen automatically through realtime
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ProductActivityFeed;